import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, Mail, Phone, FileText, Search, X, Filter, ChevronDown, ChevronUp, ArrowUpDown,
  ArrowUp, ArrowDown, Users as UsersIcon, Download, Upload, Trash2, FolderUp,
  File as FileIcon, FileImage, Loader2, ExternalLink, MapPin, Sparkles, Activity,
  Euro, Calendar as CalIcon, MessageCircle, TrendingUp, Eye, EyeOff, FileDown,
  FileSignature, Receipt, FilePlus, History, CheckCircle2, Plus, UserPlus, Briefcase, Mail as MailIcon, Copy,
  ArrowLeft, KeyRound, PenSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

/* -------- Helpers -------- */
const fmtEUR = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`;
const daysSince = (iso) => {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.round(ms / 86400000));
};
const freshness = (iso) => {
  const d = daysSince(iso);
  if (d === null) return { label: "—", tone: "neutral" };
  if (d <= 7) return { label: "Cette semaine", tone: "good" };
  if (d <= 30) return { label: `il y a ${d} j`, tone: "ok" };
  if (d <= 90) return { label: `il y a ${d} j`, tone: "warn" };
  return { label: `il y a ${d} j`, tone: "cold" };
};
const toneColor = {
  good:    { bg: "#A7F3D044", text: "#065F46" },
  ok:      { bg: "#06B6D41A", text: "#06B6D4" },
  warn:    { bg: "#FFDCA866", text: "#92400E" },
  cold:    { bg: "#FEE2E2",  text: "#B91C1C" },
  neutral: { bg: "#F1F5F9",  text: "#64748B" },
};
const exchangeFreq = (count90d) => {
  if (count90d >= 8) return { label: "Très active", tone: "good" };
  if (count90d >= 4) return { label: "Active", tone: "ok" };
  if (count90d >= 1) return { label: "Faible", tone: "warn" };
  return { label: "Dormante", tone: "cold" };
};

export default function AdminEntrepreneurs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("entrepreneurs"); // 'entrepreneurs' | 'companies'
  const [active, setActive] = useState(null); // company id or entrepreneur id selected
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    sector: "",
    status: "all", // all | active | dormant
    hasInvoice: "all", // all | yes | no
    minCA: "",
  });
  const [sort, setSort] = useState({ key: "last_exchange_at", dir: "desc" });
  const [showCreate, setShowCreate] = useState(false);
  const [showMerge, setShowMerge] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/entrepreneurs")
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  /* Flatten companies for "Par entreprise" view */
  const allCompanies = useMemo(() => {
    const out = [];
    items.forEach((e) => {
      (e.companies || []).forEach((c) => out.push({
        ...c,
        entrepreneur_id: e.id,
        entrepreneur_name: e.full_name,
        entrepreneur_email: e.email,
        entrepreneur_phone: e.phone,
      }));
    });
    return out;
  }, [items]);

  /* Unique sectors */
  const sectors = useMemo(() => {
    const s = new Set();
    allCompanies.forEach((c) => { if (c.sector) s.add(c.sector); });
    return Array.from(s).sort();
  }, [allCompanies]);

  /* Filtering + sorting */
  const filteredCompanies = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = allCompanies.filter((c) => {
      if (q) {
        const blob = [c.name, c.sector, c.email, c.phone, c.siret, c.tva, c.website, c.address, c.city, c.entrepreneur_name, c.entrepreneur_email].filter(Boolean).join(" ").toLowerCase();
        if (!blob.includes(q)) return false;
      }
      if (filters.sector && c.sector !== filters.sector) return false;
      if (filters.status === "active" && !c.is_active) return false;
      if (filters.status === "dormant" && c.is_active) return false;
      if (filters.hasInvoice === "yes" && (c.invoices_count || 0) === 0) return false;
      if (filters.hasInvoice === "no" && (c.invoices_count || 0) > 0) return false;
      if (filters.minCA && (c.ca_total_ttc || 0) < parseFloat(filters.minCA)) return false;
      return true;
    });
    list.sort((a, b) => {
      const va = a[sort.key] ?? "";
      const vb = b[sort.key] ?? "";
      let cmp;
      if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb));
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [allCompanies, search, filters, sort]);

  const filteredEntrepreneurs = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = items.filter((e) => {
      if (q) {
        const blob = [e.full_name, e.email, e.phone, ...(e.companies || []).flatMap((c) => [c.name, c.sector, c.siret])].filter(Boolean).join(" ").toLowerCase();
        if (!blob.includes(q)) return false;
      }
      if (filters.status === "active" && !e.is_active) return false;
      if (filters.status === "dormant" && e.is_active) return false;
      if (filters.status === "prospect" && (e.crm_status || "client") !== "prospect") return false;
      if (filters.status === "client_only" && (e.crm_status || "client") !== "client") return false;
      if (filters.status === "archive" && (e.crm_status || "client") !== "archive") return false;
      if (filters.sector && !(e.companies || []).some((c) => c.sector === filters.sector)) return false;
      if (filters.hasInvoice === "yes" && !(e.companies || []).some((c) => (c.invoices_count || 0) > 0)) return false;
      if (filters.hasInvoice === "no" && (e.companies || []).some((c) => (c.invoices_count || 0) > 0)) return false;
      if (filters.minCA && (e.ca_total_ttc || 0) < parseFloat(filters.minCA)) return false;
      return true;
    });
    list.sort((a, b) => {
      const va = a[sort.key] ?? "";
      const vb = b[sort.key] ?? "";
      let cmp;
      if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb));
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [items, search, filters, sort]);

  /* Aggregate KPIs across filtered view */
  const kpis = useMemo(() => {
    const list = view === "companies" ? filteredCompanies : filteredEntrepreneurs;
    const sum = (k) => list.reduce((a, x) => a + (x[k] || 0), 0);
    return {
      count: list.length,
      ca_paid: sum("ca_paid_ttc"),
      ca_pending: sum("ca_pending_ttc"),
      ca_total: sum("ca_total_ttc"),
      active: list.filter((x) => x.is_active).length,
    };
  }, [filteredEntrepreneurs, filteredCompanies, view]);

  const setSortKey = (key) => {
    setSort((s) => s.key === key ? { ...s, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" });
  };

  const openDetail = (id, isCompany) => {
    setActive({ id, isCompany });
    setDetail(null);
    const eid = isCompany ? allCompanies.find((c) => c.id === id)?.entrepreneur_id : id;
    if (!eid) return;
    api.get(`/admin/entrepreneurs/${eid}`).then((r) => setDetail({ ...r.data, _focus_company_id: isCompany ? id : null })).catch(() => toast.error("Erreur"));
  };
  const closeDetail = () => { setActive(null); setDetail(null); };

  /* CSV export — uses currently filtered & sorted list */
  const exportCSV = () => {
    const list = view === "companies" ? filteredCompanies : filteredEntrepreneurs;
    if (list.length === 0) { toast.error("Aucune donnée à exporter"); return; }
    const esc = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    let rows;
    if (view === "companies") {
      rows = [
        ["Entreprise", "Secteur", "SIRET", "TVA", "Site", "Email", "Téléphone", "Ville", "Adresse",
          "Entrepreneur", "Email entrepreneur", "Téléphone entrepreneur",
          "CA total TTC", "CA payé TTC", "CA en cours TTC",
          "Devis", "Devis signés", "Montant devis total",
          "Factures", "Factures payées", "RDV", "Briefs", "Documents",
          "Dernier échange", "Événements 90j", "Actif", "Créée le"],
        ...list.map((c) => [
          c.name, c.sector, c.siret, c.tva, c.website, c.email, c.phone, c.city, c.address,
          c.entrepreneur_name, c.entrepreneur_email, c.entrepreneur_phone,
          c.ca_total_ttc, c.ca_paid_ttc, c.ca_pending_ttc,
          c.proposals_count, c.proposals_signed_count, c.proposals_total_ttc,
          c.invoices_count, c.invoices_paid_count, c.appointments_count, c.briefs_count, c.documents_count,
          (c.last_exchange_at || "").slice(0, 10), c.exchange_count_90d, c.is_active ? "Oui" : "Non", (c.created_at || "").slice(0, 10),
        ]),
      ];
    } else {
      rows = [
        ["Entrepreneur", "Email", "Téléphone", "Nb entreprises", "Entreprises",
          "CA total TTC", "CA payé TTC", "CA en cours TTC",
          "Briefs", "Dernier échange", "Événements 90j", "Actif", "Inscrit le"],
        ...list.map((e) => [
          e.full_name, e.email, e.phone, e.companies_count,
          (e.companies || []).map((c) => c.name).join(" | "),
          e.ca_total_ttc, e.ca_paid_ttc, e.ca_pending_ttc,
          e.briefs_count, (e.last_exchange_at || "").slice(0, 10), e.exchange_count_90d, e.is_active ? "Oui" : "Non", (e.created_at || "").slice(0, 10),
        ]),
      ];
    }
    const csv = rows.map((r) => r.map(esc).join(";")).join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const today = new Date().toISOString().slice(0, 10);
    a.href = url; a.download = `besson-clients-${view}-${today}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success(`${list.length} ligne${list.length > 1 ? "s" : ""} exportée${list.length > 1 ? "s" : ""}`);
  };

  return (
    <div data-testid="admin-entrepreneurs">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="display text-2xl sm:text-3xl text-[#0A1128] font-extrabold">Clients</h1>
          <p className="text-sm text-[#64748B] mt-1">Base contacts entrepreneurs + entreprises avec CA généré, dernier échange, fréquence d'activité.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowCreate(true)} size="sm" className="bg-[#06B6D4] hover:bg-[#67E8F9] text-[#0A1128] font-bold" data-testid="crm-create-client-btn">
            <Plus size={13} className="mr-1.5" />Créer un client
          </Button>
          <Button onClick={exportCSV} variant="outline" size="sm" data-testid="crm-export-csv" className="border-[#06B6D4] text-[#06B6D4] hover:bg-[#06B6D4]/10">
            <FileDown size={13} className="mr-1.5" />Exporter CSV
          </Button>
          <Button onClick={() => setShowMerge(true)} variant="outline" size="sm" data-testid="crm-merge-btn" className="border-[#FFDCA8] text-[#92400E] hover:bg-[#FFDCA8]/20">
            <Activity size={13} className="mr-1.5" />Fusionner doublons
          </Button>
          <div className="inline-flex rounded-full bg-[#FAFAFA] border border-black/8 p-1">
            <button onClick={() => setView("entrepreneurs")} data-testid="crm-view-entrepreneurs" className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${view === "entrepreneurs" ? "bg-[#0A1128] text-white" : "text-[#64748B] hover:text-[#0A1128]"}`}>
              <UsersIcon size={12} className="inline mr-1.5" />Par entrepreneur
            </button>
            <button onClick={() => setView("companies")} data-testid="crm-view-companies" className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${view === "companies" ? "bg-[#0A1128] text-white" : "text-[#64748B] hover:text-[#0A1128]"}`}>
              <Building2 size={12} className="inline mr-1.5" />Par entreprise
            </button>
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi label={view === "companies" ? "Entreprises" : "Entrepreneurs"} value={kpis.count} sub={`${kpis.active} actifs (90j)`} icon={view === "companies" ? Building2 : UsersIcon} color="#06B6D4" />
        <Kpi label="CA encaissé" value={fmtEUR(kpis.ca_paid)} sub="factures payées" icon={Euro} color="#A7F3D0" />
        <Kpi label="CA en cours" value={fmtEUR(kpis.ca_pending)} sub="factures à régler" icon={TrendingUp} color="#FFDCA8" />
        <Kpi label="CA total généré" value={fmtEUR(kpis.ca_total)} sub="depuis le début" icon={Activity} color="#67E8F9" />
      </div>

      {/* Search + filters */}
      <div className="mt-5 bg-white rounded-2xl border border-black/8 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
            <Input data-testid="crm-search" placeholder="Rechercher nom, email, SIRET, secteur, ville..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select data-testid="crm-filter-sector" value={filters.sector} onChange={(e) => setFilters({ ...filters, sector: e.target.value })} className="border border-black/10 rounded-md px-3 py-2 text-sm bg-white">
            <option value="">Tous secteurs</option>
            {sectors.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="crm-filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="border border-black/10 rounded-md px-3 py-2 text-sm bg-white">
            <option value="all">Tous</option>
            <option value="prospect">Prospects</option>
            <option value="client_only">Clients</option>
            <option value="archive">Archivés</option>
            <option value="active">Actifs (90j)</option>
            <option value="dormant">Dormants</option>
          </select>
          <select data-testid="crm-filter-invoice" value={filters.hasInvoice} onChange={(e) => setFilters({ ...filters, hasInvoice: e.target.value })} className="border border-black/10 rounded-md px-3 py-2 text-sm bg-white">
            <option value="all">Tous (facturation)</option>
            <option value="yes">Avec factures</option>
            <option value="no">Sans facture</option>
          </select>
          <Input data-testid="crm-filter-min-ca" type="number" placeholder="CA min (€)" value={filters.minCA} onChange={(e) => setFilters({ ...filters, minCA: e.target.value })} className="max-w-[120px]" />
          {(search || filters.sector || filters.status !== "all" || filters.hasInvoice !== "all" || filters.minCA) && (
            <Button variant="outline" size="sm" onClick={() => { setSearch(""); setFilters({ sector: "", status: "all", hasInvoice: "all", minCA: "" }); }} data-testid="crm-reset-filters">
              <X size={12} className="mr-1" />Réinitialiser
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="bg-white rounded-2xl p-10 text-center text-sm text-[#64748B] mt-5"><Loader2 className="animate-spin inline mr-2" size={14} />Chargement…</div>
      ) : view === "entrepreneurs" ? (
        <EntrepreneursTable items={filteredEntrepreneurs} sort={sort} setSortKey={setSortKey} onOpen={(id) => openDetail(id, false)} activeId={active?.id} />
      ) : (
        <CompaniesTable items={filteredCompanies} sort={sort} setSortKey={setSortKey} onOpen={(id) => openDetail(id, true)} activeId={active?.id} />
      )}

      {/* Detail drawer */}
      {active && (
        <DetailDrawer detail={detail} loading={!detail} onClose={closeDetail} onChange={load} />
      )}

      {showCreate && (
        <CreateClientModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
      {showMerge && (
        <MergeClientsModal
          onClose={() => setShowMerge(false)}
          onMerged={() => { setShowMerge(false); load(); }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-2xl border border-black/8 p-4" data-testid={`crm-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-[10px] font-bold tracking-widest uppercase" style={{ color }}>
        <Icon size={12} /> {label}
      </div>
      <div className="display text-2xl font-extrabold text-[#0A1128] mt-1">{value}</div>
      <div className="text-[10px] text-[#64748B] mt-0.5">{sub}</div>
    </div>
  );
}

function SortHeader({ label, k, sort, setSortKey, align = "left" }) {
  const active = sort.key === k;
  return (
    <th className={`px-3 py-2 text-${align}`}>
      <button onClick={() => setSortKey(k)} data-testid={`crm-sort-${k}`} className={`inline-flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase ${active ? "text-[#06B6D4]" : "text-[#64748B] hover:text-[#0A1128]"}`}>
        {label}
        {active ? (sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />) : <ArrowUpDown size={11} className="opacity-50" />}
      </button>
    </th>
  );
}

function EntrepreneursTable({ items, sort, setSortKey, onOpen, activeId }) {
  return (
    <div className="mt-5 bg-white rounded-2xl border border-black/8 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="crm-table-entrepreneurs">
          <thead className="bg-[#FAFAFA] border-b border-black/8">
            <tr>
              <SortHeader label="Entrepreneur" k="full_name" sort={sort} setSortKey={setSortKey} />
              <SortHeader label="Contact" k="email" sort={sort} setSortKey={setSortKey} />
              <SortHeader label="Entreprises" k="companies_count" sort={sort} setSortKey={setSortKey} align="right" />
              <SortHeader label="CA payé" k="ca_paid_ttc" sort={sort} setSortKey={setSortKey} align="right" />
              <SortHeader label="CA en cours" k="ca_pending_ttc" sort={sort} setSortKey={setSortKey} align="right" />
              <SortHeader label="Dernier échange" k="last_exchange_at" sort={sort} setSortKey={setSortKey} />
              <SortHeader label="Fréquence (90j)" k="exchange_count_90d" sort={sort} setSortKey={setSortKey} />
              <SortHeader label="Inscrit le" k="created_at" sort={sort} setSortKey={setSortKey} />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-sm text-[#64748B]">Aucun entrepreneur ne correspond aux filtres.</td></tr>
            )}
            {items.map((e) => {
              const fr = freshness(e.last_exchange_at);
              const ef = exchangeFreq(e.exchange_count_90d);
              const isActive = activeId === e.id;
              return (
                <tr key={e.id} onClick={() => onOpen(e.id)} data-testid={`entrepreneur-${e.id}`} className={`border-b border-black/5 cursor-pointer transition-colors ${isActive ? "bg-[#06B6D4]/5" : "hover:bg-[#FAFAFA]"}`}>
                  <td className="px-3 py-3">
                    <div className="display font-extrabold text-[#0A1128]">{e.full_name}</div>
                    <div className="text-[11px] text-[#64748B] truncate max-w-[200px]">
                      {(e.companies || []).map((c) => c.name).join(", ") || "—"}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-xs">
                    <div className="text-[#0A1128] truncate"><Mail size={11} className="inline mr-1 text-[#06B6D4]" />{e.email}</div>
                    {e.phone && <div className="text-[#64748B]"><Phone size={11} className="inline mr-1" />{e.phone}</div>}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums font-bold">{e.companies_count}</td>
                  <td className="px-3 py-3 text-right tabular-nums font-bold text-[#065F46]">{fmtEUR(e.ca_paid_ttc)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-[#92400E]">{fmtEUR(e.ca_pending_ttc)}</td>
                  <td className="px-3 py-3"><Pill {...fr} /></td>
                  <td className="px-3 py-3"><Pill {...ef} sub={`${e.exchange_count_90d} évén.`} /></td>
                  <td className="px-3 py-3 text-xs text-[#64748B]">{(e.created_at || "").slice(0, 10)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CompaniesTable({ items, sort, setSortKey, onOpen, activeId }) {
  return (
    <div className="mt-5 bg-white rounded-2xl border border-black/8 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="crm-table-companies">
          <thead className="bg-[#FAFAFA] border-b border-black/8">
            <tr>
              <SortHeader label="Entreprise" k="name" sort={sort} setSortKey={setSortKey} />
              <SortHeader label="Secteur" k="sector" sort={sort} setSortKey={setSortKey} />
              <SortHeader label="Entrepreneur" k="entrepreneur_name" sort={sort} setSortKey={setSortKey} />
              <SortHeader label="CA total" k="ca_total_ttc" sort={sort} setSortKey={setSortKey} align="right" />
              <SortHeader label="Payé" k="ca_paid_ttc" sort={sort} setSortKey={setSortKey} align="right" />
              <SortHeader label="Devis" k="proposals_count" sort={sort} setSortKey={setSortKey} align="right" />
              <SortHeader label="RDV" k="appointments_count" sort={sort} setSortKey={setSortKey} align="right" />
              <SortHeader label="Dernier échange" k="last_exchange_at" sort={sort} setSortKey={setSortKey} />
              <SortHeader label="Fréquence (90j)" k="exchange_count_90d" sort={sort} setSortKey={setSortKey} />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-sm text-[#64748B]">Aucune entreprise ne correspond aux filtres.</td></tr>
            )}
            {items.map((c) => {
              const fr = freshness(c.last_exchange_at);
              const ef = exchangeFreq(c.exchange_count_90d);
              const isActive = activeId === c.id;
              return (
                <tr key={c.id} onClick={() => onOpen(c.id)} data-testid={`company-row-${c.id}`} className={`border-b border-black/5 cursor-pointer transition-colors ${isActive ? "bg-[#06B6D4]/5" : "hover:bg-[#FAFAFA]"}`}>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      {c.logo_url ? (
                        <img src={c.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-[#0A1128] text-white flex items-center justify-center text-xs font-bold shrink-0">{(c.name || "?").slice(0, 1).toUpperCase()}</div>
                      )}
                      <div className="min-w-0">
                        <div className="display font-extrabold text-[#0A1128] truncate">{c.name}</div>
                        {c.siret && <div className="text-[10px] text-[#64748B]">SIRET {c.siret}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-xs text-[#475569]">{c.sector || "—"}</td>
                  <td className="px-3 py-3 text-xs">
                    <div className="font-bold text-[#0A1128]">{c.entrepreneur_name}</div>
                    <div className="text-[#64748B] truncate max-w-[160px]">{c.entrepreneur_email}</div>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums font-extrabold text-[#0A1128]">{fmtEUR(c.ca_total_ttc)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-[#065F46]">{fmtEUR(c.ca_paid_ttc)}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{c.proposals_count} <span className="text-[10px] text-[#64748B]">({c.proposals_signed_count} ✓)</span></td>
                  <td className="px-3 py-3 text-right tabular-nums">{c.appointments_count}</td>
                  <td className="px-3 py-3"><Pill {...fr} /></td>
                  <td className="px-3 py-3"><Pill {...ef} sub={`${c.exchange_count_90d} évén.`} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Pill({ label, tone, sub }) {
  const c = toneColor[tone] || toneColor.neutral;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: c.bg, color: c.text }}>
      {label}{sub && <span className="opacity-75 normal-case font-medium tracking-normal">· {sub}</span>}
    </span>
  );
}

/* -------- Detail (full page modal inside content area) -------- */
function DetailDrawer({ detail, loading, onClose, onChange }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const ent = detail?.entrepreneur;
  const status = ent?.crm_status || "client";

  const setStatus = async (next) => {
    setBusy(true);
    try {
      await api.patch(`/admin/entrepreneurs/${ent.id}`, { crm_status: next });
      toast.success(`Statut → ${next === "client" ? "Client" : next === "prospect" ? "Prospect" : "Archivé"}`);
      onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const grantPortalAccess = async () => {
    if (!window.confirm(`Envoyer un email d'activation Espace Entreprise à ${ent.email} ?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/entrepreneurs/${ent.id}/grant-portal`);
      toast.success(r.data?.email_sent ? "Email d'activation envoyé ✉️" : "Lien généré (email non configuré)");
      onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const removeClient = async () => {
    const sure = window.prompt(`⚠ SUPPRESSION DÉFINITIVE\n\nClient : ${ent.full_name}\nEmail : ${ent.email}\n\nToutes les entreprises, devis, factures, RDV, briefs, projets et événements seront supprimés.\n\nTapez "SUPPRIMER" pour confirmer :`);
    if (sure !== "SUPPRIMER") return;
    const force = window.confirm("Supprimer même les factures payées ? (forçage légal — annuler pour archiver à la place)");
    setBusy(true);
    try {
      await api.delete(`/admin/entrepreneurs/${ent.id}${force ? "?force=true" : ""}`);
      toast.success("Client supprimé");
      onClose();
      onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 lg:left-[288px] z-[80] bg-[#F0F4FA] flex flex-col"
      data-testid="crm-drawer"
    >
      <div className="px-5 py-3 border-b border-black/8 flex items-center justify-between sticky top-0 bg-white z-10 shadow-sm gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap min-w-0 flex-1">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg hover:bg-[#06B6D4]/10 inline-flex items-center gap-1.5 text-sm font-bold text-[#06B6D4] border border-[#06B6D4]/30 shrink-0" data-testid="crm-drawer-back"><ArrowLeft size={14} />Retour</button>
          <div className="display text-lg font-extrabold text-[#0A1128] truncate">Fiche client {ent?.full_name ? `— ${ent.full_name}` : ""}</div>
          {ent && (
            <div className="inline-flex items-center gap-1 bg-[#FAFAFA] border border-black/10 rounded-full p-0.5" data-testid="crm-status-switch">
              {[
                { v: "prospect", label: "Prospect", color: "bg-[#FFDCA8] text-[#92400E]" },
                { v: "client",   label: "Client",   color: "bg-[#A7F3D0] text-[#065F46]" },
                { v: "archive",  label: "Archivé",  color: "bg-[#E5E7EB] text-[#374151]" },
              ].map((opt) => (
                <button key={opt.v} disabled={busy} onClick={() => setStatus(opt.v)} data-testid={`crm-status-${opt.v}`}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${status === opt.v ? opt.color : "text-[#64748B] hover:bg-black/5"}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {ent && (
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={grantPortalAccess} disabled={busy} title="Envoyer un email pour qu'il personnalise son mot de passe portail" className="px-3 py-1.5 rounded-lg hover:bg-[#06B6D4]/10 inline-flex items-center gap-1.5 text-xs font-bold text-[#06B6D4] border border-[#06B6D4]/30" data-testid="crm-grant-portal"><KeyRound size={13} />Activer l'accès portail</button>
            <button onClick={removeClient} disabled={busy} title="Supprimer définitivement" className="px-2.5 py-1.5 rounded-lg hover:bg-red-50 inline-flex items-center gap-1.5 text-xs font-bold text-red-600 border border-red-200" data-testid="crm-delete-client"><Trash2 size={13} />Supprimer</button>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-10 text-center text-sm text-[#64748B]"><Loader2 className="animate-spin inline mr-2" />Chargement…</div>
        ) : detail ? (
          <div className="p-5 space-y-5 max-w-6xl mx-auto">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="display text-2xl font-extrabold text-[#0A1128] flex items-center gap-2 flex-wrap">
                  {detail.entrepreneur.full_name}
                  <button
                    onClick={() => setEditing(true)}
                    title="Modifier la fiche client"
                    data-testid="crm-edit-entrepreneur"
                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[#06B6D4]/10 hover:bg-[#06B6D4]/20 text-[#06B6D4] inline-flex items-center gap-1 transition-colors"
                  >
                    <PenSquare size={11} /> Modifier
                  </button>
                </div>
                <div className="text-sm text-[#64748B] flex items-center gap-3 mt-1 flex-wrap">
                  <a href={`mailto:${detail.entrepreneur.email}`} className="flex items-center gap-1 hover:text-[#06B6D4]"><Mail size={12} />{detail.entrepreneur.email}</a>
                  {detail.entrepreneur.phone && <a href={`tel:${detail.entrepreneur.phone}`} className="flex items-center gap-1 hover:text-[#06B6D4]"><Phone size={12} />{detail.entrepreneur.phone}</a>}
                </div>
                <div className="text-[10px] text-[#94a3b8] mt-1">Inscrit le {(detail.entrepreneur.created_at || "").slice(0, 10)}</div>
              </div>
              <NewClientEventButton entrepreneurId={detail.entrepreneur.id} companies={detail.companies || []} onCreated={onChange} />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="text-xs font-bold tracking-widest uppercase text-[#06B6D4]">Entreprises ({detail.companies.length})</div>
                <AttachCompanyButton entrepreneurId={ent.id} onAttached={onChange} />
              </div>
              <div className="space-y-2">
                {detail.companies.length === 0 && <div className="text-sm text-[#64748B]">Aucune entreprise enregistrée.</div>}
                {detail.companies.map((c) => (
                  <AdminCompanyBlock key={c.id} c={c} defaultOpen={detail._focus_company_id === c.id} onChange={onChange} />
                ))}
              </div>
            </div>

            {(detail.projects && detail.projects.length > 0) && (
              <div>
                <div className="text-xs font-bold tracking-widest uppercase text-[#06B6D4] mb-2">Projets en cours ({detail.projects.length})</div>
                <div className="space-y-2">
                  {detail.projects.map((p) => {
                    const pct = Number(p.progress_pct || 0);
                    const overdue = p.deadline && p.status !== "cloture" && new Date(p.deadline) < new Date();
                    return (
                      <div key={p.id} className="bg-white rounded-xl p-3 border border-black/8" data-testid={`detail-project-${p.id}`}>
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div className="min-w-0">
                            <div className="display font-bold text-[#0A1128]">{p.title}</div>
                            <div className="text-[11px] text-[#64748B] mt-0.5 flex items-center gap-2 flex-wrap">
                              <span>Dernier échange : <b className="text-[#0A1128]">{(p.last_exchange_at || "—").slice(0, 10)}</b></span>
                              {p.deadline && (
                                <span className={overdue ? "text-red-600 font-bold" : "text-[#06B6D4] font-bold"}>
                                  · Deadline : {p.deadline.slice(0, 10)}{overdue ? " ⚠" : ""}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="text-[10px] uppercase font-bold bg-[#06B6D4] text-white px-2 py-0.5 rounded-full">{p.status}</span>
                        </div>
                        <div className="mt-2 h-1.5 rounded-full bg-[#FAFAFA] overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-[#06B6D4] to-[#67E8F9]" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="text-[10px] text-[#64748B] mt-1">{pct}% — {p.tasks_done}/{p.tasks_total} tâches</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {detail.timeline && detail.timeline.length > 0 && (
              <ClientTimeline events={detail.timeline} />
            )}

            {detail.briefs.length > 0 && (
              <div>
                <div className="text-xs font-bold tracking-widest uppercase text-[#06B6D4] mb-2">Briefs soumis ({detail.briefs.length})</div>
                <div className="space-y-2">
                  {detail.briefs.map((b) => (
                    <div key={b.id} className="bg-[#FAFAFA] rounded-xl p-3 border border-black/5 flex items-start gap-3">
                      <FileText size={18} className="text-[#06B6D4] mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="display font-bold text-[#0A1128]">{b.title || (b.project_idea || "").slice(0, 80)}</div>
                        <div className="text-xs text-[#64748B]">{b.company_name || "—"} · {b.priority || "—"} · {(b.created_at || "").slice(0, 10)}</div>
                      </div>
                      <span className="text-[10px] uppercase font-bold bg-[#FFDCA8] text-[#92400E] px-2 py-0.5 rounded-full">{b.status || "submitted"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
      {editing && ent && (
        <EditEntrepreneurModal
          entrepreneur={ent}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); onChange && onChange(); }}
        />
      )}
    </div>
  );
}

function EditEntrepreneurModal({ entrepreneur, onClose, onSaved }) {
  const initial = useMemo(() => {
    const parts = String(entrepreneur.full_name || "").trim().split(/\s+/);
    return {
      first_name: entrepreneur.first_name || parts[0] || "",
      last_name: entrepreneur.last_name || (parts.length > 1 ? parts.slice(1).join(" ") : ""),
      email: entrepreneur.email || "",
      phone: entrepreneur.phone || "",
      mobile: entrepreneur.mobile || "",
      notes: entrepreneur.notes || "",
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entrepreneur.id]);
  const [draft, setDraft] = useState(initial);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!draft.first_name.trim() && !draft.last_name.trim()) return toast.error("Prénom ou nom requis");
    if (!draft.email.trim() || !draft.email.includes("@")) return toast.error("Email invalide");
    setBusy(true);
    try {
      const body = {
        first_name: draft.first_name.trim(),
        last_name: draft.last_name.trim(),
        email: draft.email.trim().toLowerCase(),
        phone: draft.phone.trim() || null,
        mobile: draft.mobile.trim() || null,
        notes: draft.notes,
      };
      await api.patch(`/admin/entrepreneurs/${entrepreneur.id}`, body);
      toast.success("Fiche client mise à jour");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-[#0A1128]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="edit-entrepreneur-modal">
      <div className="bg-white rounded-3xl w-full max-w-xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-black/8 flex items-center justify-between">
          <div className="display font-extrabold text-[#0A1128]">Modifier la fiche client</div>
          <button onClick={onClose} className="text-[#64748B] hover:text-[#0A1128]" data-testid="edit-entrepreneur-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Prénom *</Label>
              <Input data-testid="edit-entrepreneur-first-name" value={draft.first_name} onChange={(e) => setDraft({ ...draft, first_name: e.target.value })} placeholder="Marie" />
            </div>
            <div>
              <Label className="text-xs">Nom *</Label>
              <Input data-testid="edit-entrepreneur-last-name" value={draft.last_name} onChange={(e) => setDraft({ ...draft, last_name: e.target.value })} placeholder="Dupont" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Email *</Label>
            <Input data-testid="edit-entrepreneur-email" type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} placeholder="marie@exemple.fr" />
            <p className="text-[10px] text-[#94a3b8] mt-1">Si vous changez l'email, le client devra utiliser le nouveau pour se connecter à son espace.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Téléphone fixe</Label>
              <Input data-testid="edit-entrepreneur-phone" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} placeholder="04 .. .. .. .." />
            </div>
            <div>
              <Label className="text-xs">Mobile</Label>
              <Input data-testid="edit-entrepreneur-mobile" value={draft.mobile} onChange={(e) => setDraft({ ...draft, mobile: e.target.value })} placeholder="06 .. .. .. .." />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes internes (non visibles client)</Label>
            <Textarea data-testid="edit-entrepreneur-notes" rows={3} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} placeholder="Informations privées sur ce client…" />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-black/8 bg-[#FAFAFA] flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="edit-entrepreneur-cancel">Annuler</Button>
          <Button onClick={save} disabled={busy} className="bg-[#0A1128] hover:bg-[#14213d] text-white" data-testid="edit-entrepreneur-save">
            {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : <CheckCircle2 size={14} className="mr-1" />}
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
}

/* -------- Attach a new company to an existing entrepreneur -------- */
function AttachCompanyButton({ entrepreneurId, onAttached }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [form, setForm] = useState({ name: "", sector: "", siret: "", tva: "", address: "", phone: "", email: "", website: "", description: "", notify: true });

  const lookup = async () => {
    const q = query.trim();
    if (q.length < 3) return toast.error("3 caractères minimum (SIRET ou raison sociale)");
    setBusy(true); setResults([]);
    try {
      const cleaned = q.replace(/\s+/g, "");
      const isNumeric = /^\d+$/.test(cleaned);
      const param = isNumeric ? (cleaned.length === 14 ? "siret" : "siren") : "q";
      const value = isNumeric ? cleaned : q;
      const r = await api.get(`/admin/company-lookup?${param}=${encodeURIComponent(value)}`);
      const items = r.data?.results || [];
      if (items.length === 0) { toast.error("Aucune entreprise trouvée"); }
      else if (items.length === 1) { applyResult(items[0]); toast.success("Entreprise trouvée"); }
      else { setResults(items); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Recherche impossible"); }
    finally { setBusy(false); }
  };

  const applyResult = (d) => {
    setForm((f) => ({
      ...f,
      name: d.nom_complet || d.denomination || d.nom_raison_sociale || f.name,
      siret: d.siret || f.siret,
      sector: d.section_activite || d.activite_principale || f.sector,
      address: [d.adresse, d.code_postal, d.commune].filter(Boolean).join(", ") || f.address,
      website: d.site_internet || f.website,
    }));
    setResults([]);
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Nom requis");
    setBusy(true);
    try {
      await api.post(`/admin/entrepreneurs/${entrepreneurId}/companies`, form);
      toast.success(form.notify ? "Entreprise ajoutée + notification envoyée" : "Entreprise ajoutée");
      setOpen(false);
      setForm({ name: "", sector: "", siret: "", tva: "", address: "", phone: "", email: "", website: "", description: "", notify: true });
      setQuery(""); setResults([]);
      onAttached && onAttached();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="attach-company-btn" className="border-[#06B6D4] text-[#06B6D4] hover:bg-[#06B6D4]/10">
        <Building2 size={13} className="mr-1" />Ajouter une entreprise
      </Button>
      {open && (
        <div className="fixed inset-0 z-[100] bg-[#0A1128]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !busy && setOpen(false)} data-testid="attach-company-modal">
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-black/8 flex items-center justify-between">
              <div className="display font-extrabold text-[#0A1128]">Rattacher une entreprise</div>
              <button onClick={() => setOpen(false)} className="text-[#64748B] hover:text-[#0A1128]"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-[#06B6D4]/8 border border-[#06B6D4]/20 rounded-xl p-3">
                <Label className="mb-1.5 block text-[10px] uppercase tracking-widest font-bold text-[#06B6D4]">Recherche entreprise (SIRET, SIREN ou raison sociale)</Label>
                <div className="flex gap-2">
                  <Input data-testid="attach-company-query" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookup(); } }} placeholder="14 chiffres ou nom (ex : Sellerie Académie)" />
                  <Button onClick={lookup} disabled={busy} variant="outline" data-testid="attach-company-search-go">{busy ? <Loader2 className="animate-spin" size={14} /> : "Rechercher"}</Button>
                </div>
                {results.length > 0 && (
                  <div className="mt-3 max-h-60 overflow-y-auto space-y-1.5" data-testid="attach-company-results">
                    {results.map((it) => (
                      <button key={it.siret || it.siren} onClick={() => applyResult(it)} className="w-full text-left bg-white hover:bg-[#06B6D4]/5 border border-black/10 hover:border-[#06B6D4] rounded-lg px-3 py-2 text-xs transition-colors" data-testid={`attach-result-${it.siren}`}>
                        <div className="font-bold text-[#0A1128]">{it.nom_complet || it.denomination || it.nom_raison_sociale}</div>
                        <div className="text-[10px] text-[#64748B] mt-0.5">
                          SIRET <span className="tabular-nums">{it.siret || "—"}</span>
                          {it.section_activite && <> · {it.section_activite}</>}
                          {(it.code_postal || it.commune) && <> · {[it.code_postal, it.commune].filter(Boolean).join(" ")}</>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2"><Label>Nom de l'entreprise *</Label><Input data-testid="attach-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>SIRET</Label><Input data-testid="attach-siret" value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} /></div>
                <div><Label>TVA intracom.</Label><Input data-testid="attach-tva" value={form.tva} onChange={(e) => setForm({ ...form, tva: e.target.value })} /></div>
                <div><Label>Secteur</Label><Input data-testid="attach-sector" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} /></div>
                <div><Label>Site web</Label><Input data-testid="attach-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></div>
                <div><Label>Email entreprise</Label><Input data-testid="attach-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Téléphone</Label><Input data-testid="attach-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div className="sm:col-span-2"><Label>Adresse</Label><Input data-testid="attach-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                <div className="sm:col-span-2"><Label>Description</Label><Textarea data-testid="attach-description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              </div>

              <label className="flex items-center gap-2 text-sm text-[#0A1128] bg-[#FAFAFA] border border-black/5 rounded-xl px-3 py-2 cursor-pointer">
                <input type="checkbox" checked={form.notify} onChange={(e) => setForm({ ...form, notify: e.target.checked })} data-testid="attach-notify" />
                Notifier l'entrepreneur par email + notification in-app ("Une nouvelle entreprise a été rattachée à votre compte")
              </label>
            </div>
            <div className="px-6 py-4 border-t border-black/8 bg-[#FAFAFA] flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Annuler</Button>
              <Button onClick={submit} disabled={busy || !form.name.trim()} className="bg-[#0A1128] hover:bg-[#14213d] text-white" data-testid="attach-company-submit">
                {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : <Building2 size={14} className="mr-1" />}
                Rattacher l'entreprise
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


/* -------- Company expandable block (kept from previous iteration) -------- */
function AdminCompanyBlock({ c: cProp, defaultOpen = false, onChange }) {
  const [c, setC] = useState(cProp); // local mirror so role edits show instantly
  useEffect(() => { setC(cProp); }, [cProp]);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [open, setOpen] = useState(defaultOpen);
  const [tab, setTab] = useState("overview"); // overview | appointments | proposals | invoices | projects | documents
  const [overview, setOverview] = useState(null);
  const [loadingOv, setLoadingOv] = useState(false);
  const [roleDraft, setRoleDraft] = useState(c.role || "");
  const [editingRole, setEditingRole] = useState(false);
  const [savingRole, setSavingRole] = useState(false);

  const loadDocs = () => api.get(`/admin/companies/${c.id}/documents`).then((r) => setDocs(r.data || [])).catch(() => {});
  const loadOverview = async () => {
    setLoadingOv(true);
    try {
      const r = await api.get(`/admin/companies/${c.id}/overview`);
      setOverview(r.data);
    } catch { /* silent */ }
    finally { setLoadingOv(false); }
  };
  useEffect(() => {
    if (!open) return;
    if (tab === "documents") loadDocs();
    if (["overview", "appointments", "proposals", "invoices", "projects"].includes(tab) && !overview) loadOverview();
    /* eslint-disable-next-line */
  }, [open, tab]);

  const saveRole = async () => {
    const newRole = roleDraft.trim();
    if (newRole === (c.role || "")) { setEditingRole(false); return; }
    setSavingRole(true);
    try {
      const r = await api.patch(`/admin/companies/${c.id}`, { role: newRole });
      setC((prev) => ({ ...prev, role: r.data?.role || newRole })); // instant local refresh
      toast.success("Rôle mis à jour");
      setEditingRole(false);
      onChange && onChange();
    } catch { toast.error("Erreur lors de la sauvegarde du rôle"); }
    finally { setSavingRole(false); }
  };

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || "").split(",", 2)[1] || "");
    r.onerror = reject; r.readAsDataURL(file);
  });

  const ALLOWED = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];
  const upload = async (file) => {
    if (!file) return;
    if (!ALLOWED.includes(file.type)) return toast.error("PDF, PNG ou JPEG uniquement");
    if (file.size > 5 * 1024 * 1024) return toast.error("Max 5 Mo");
    setUploading(true);
    try {
      const b64 = await fileToBase64(file);
      await api.post(`/admin/companies/${c.id}/documents`, { filename: file.name, mime_type: file.type, data_base64: b64 });
      toast.success("Document envoyé au client");
      loadDocs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setUploading(false); }
  };

  const removeDoc = async (d) => {
    if (!window.confirm(`Supprimer "${d.filename}" ?`)) return;
    try { await api.delete(`/admin/documents/${d.id}`); toast.success("Supprimé"); loadDocs(); }
    catch { toast.error("Erreur"); }
  };

  const downloadDoc = async (d) => {
    try {
      const url = `${window.location.origin}/api/admin/documents/${d.id}/download`;
      const token = localStorage.getItem("besson_admin_token");
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl; a.download = d.filename; a.target = "_blank";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch { toast.error("Erreur"); }
  };

  const k = overview?.kpis || {};

  return (
    <div data-testid={`adm-company-${c.id}`} className="bg-[#FAFAFA] rounded-xl border border-black/5 overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-start gap-3 p-3 text-left hover:bg-white transition-colors">
        <div className="flex-shrink-0">
          {c.logo_url ? <img src={c.logo_url} alt={c.name} className="w-10 h-10 rounded-lg object-cover" /> : <Building2 size={28} className="text-[#06B6D4]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="display font-bold text-[#0A1128] flex items-center gap-2 flex-wrap">
            {c.name}
            {c.role && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#06B6D4]/10 text-[#06B6D4]">{c.role}</span>}
          </div>
          <div className="text-xs text-[#64748B]">{c.sector || "Secteur non renseigné"}</div>
          {c.website && <span className="text-xs text-[#06B6D4]">{c.website}</span>}
        </div>
        <div className="text-[10px] text-[#94a3b8] text-right">{(c.accesses || []).length} accès · {open ? "fermer" : "+"}</div>
      </button>

      {open && (
        <div className="border-t border-black/5 bg-white">
          {/* Tabs nav */}
          <div className="flex flex-wrap gap-1 px-3 pt-3 border-b border-black/5">
            {[
              { id: "overview", label: "Vue d'ensemble", icon: Activity, count: null },
              { id: "appointments", label: "RDV", icon: CalIcon, count: k.appointments_count },
              { id: "proposals", label: "Devis", icon: FileSignature, count: k.proposals_count },
              { id: "invoices", label: "Factures", icon: Receipt, count: k.invoices_count },
              { id: "projects", label: "Projets", icon: Briefcase, count: k.projects_count },
              { id: "documents", label: "Documents", icon: FolderUp, count: null },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`adm-comp-tab-${t.id}-${c.id}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-t-lg transition-colors ${
                  tab === t.id
                    ? "bg-[#0A1128] text-white"
                    : "bg-transparent text-[#64748B] hover:bg-[#FAFAFA]"
                }`}
              >
                <t.icon size={12} />
                {t.label}
                {t.count !== null && t.count !== undefined && (
                  <span className={`ml-0.5 px-1 rounded text-[9px] ${tab === t.id ? "bg-white/20" : "bg-[#06B6D4]/10 text-[#06B6D4]"}`}>{t.count}</span>
                )}
              </button>
            ))}
          </div>

          <div className="p-3">
            {tab === "overview" && (
              <div className="space-y-4">
                {/* Rôle entrepreneur */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase font-bold tracking-widest text-[#64748B]">Rôle de l'entrepreneur :</span>
                  {editingRole ? (
                    <>
                      <Input
                        autoFocus
                        value={roleDraft}
                        onChange={(e) => setRoleDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveRole(); if (e.key === "Escape") { setRoleDraft(c.role || ""); setEditingRole(false); } }}
                        placeholder="Gérant, Présidente, Directeur commercial…"
                        className="h-7 text-xs max-w-xs"
                        data-testid={`adm-comp-role-input-${c.id}`}
                      />
                      <Button size="sm" onClick={saveRole} disabled={savingRole} data-testid={`adm-comp-role-save-${c.id}`} className="h-7 text-[11px]">
                        {savingRole ? <Loader2 className="animate-spin" size={11} /> : "Enregistrer"}
                      </Button>
                      <button onClick={() => { setRoleDraft(c.role || ""); setEditingRole(false); }} className="text-[11px] text-[#64748B] hover:underline">Annuler</button>
                    </>
                  ) : (
                    <button onClick={() => { setRoleDraft(c.role || ""); setEditingRole(true); }} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#06B6D4] hover:underline" data-testid={`adm-comp-role-edit-${c.id}`}>
                      {c.role || <span className="italic text-[#94a3b8]">+ ajouter un rôle</span>}
                    </button>
                  )}
                </div>

                {/* KPI tiles */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <KpiTile label="Signé" value={fmtEUR(k.signed_total_ttc)} sub={`${k.proposals_count || 0} devis`} color="#06B6D4" />
                  <KpiTile label="Encaissé" value={fmtEUR(k.paid_total_ttc)} sub={`${k.invoices_count || 0} factures`} color="#A7F3D0" />
                  <KpiTile label="En attente" value={fmtEUR(k.pending_total_ttc)} sub="à régler" color="#FFDCA8" />
                  <KpiTile label="Projets" value={k.projects_count || 0} sub={`${k.appointments_count || 0} RDV`} color="#0A1128" />
                </div>

                <CompanyEnrichment company={c} />
              </div>
            )}

            {tab === "appointments" && (
              loadingOv ? <LoadingRow /> : <RelatedList kind="appointments" items={overview?.appointments || []} companyId={c.id} />
            )}
            {tab === "proposals" && (
              loadingOv ? <LoadingRow /> : <RelatedList kind="proposals" items={overview?.proposals || []} companyId={c.id} />
            )}
            {tab === "invoices" && (
              loadingOv ? <LoadingRow /> : <RelatedList kind="invoices" items={overview?.invoices || []} companyId={c.id} />
            )}
            {tab === "projects" && (
              loadingOv ? <LoadingRow /> : <RelatedList kind="projects" items={overview?.projects || []} companyId={c.id} />
            )}

            {tab === "documents" && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <FolderUp size={14} className="text-[#06B6D4]" />
                  <span className="text-xs font-bold tracking-widest uppercase text-[#06B6D4]">Documents partagés</span>
                  <label className="ml-auto cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0A1128] hover:bg-[#14213d] text-white text-[11px] font-bold transition-colors" data-testid={`adm-doc-upload-${c.id}`}>
                    {uploading ? <Loader2 className="animate-spin" size={11} /> : <Upload size={11} />} Envoyer
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                      className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }}
                      disabled={uploading}
                    />
                  </label>
                </div>
                {docs.length === 0 ? (
                  <div className="text-xs text-[#64748B] text-center py-3">Aucun document partagé.</div>
                ) : (
                  <ul className="divide-y divide-black/5">
                    {docs.map((d) => (
                      <li key={d.id} data-testid={`adm-doc-${d.id}`} className="flex items-center gap-2 py-2">
                        <div className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#FAFAFA] border border-black/8 text-[#06B6D4]">
                          {String(d.mime_type || "").startsWith("image/") ? <FileImage size={14} /> : <FileIcon size={14} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-[#0A1128] truncate">{d.filename}</div>
                          <div className="text-[10px] text-[#64748B] flex items-center gap-2">
                            <span className="font-semibold uppercase tracking-wider text-[9px] px-1 py-0.5 rounded" style={{ background: d.uploaded_by === "admin" ? "#06B6D422" : "#A7F3D044", color: d.uploaded_by === "admin" ? "#06B6D4" : "#065F46" }}>
                              {d.uploaded_by === "admin" ? "BESSON" : "Client"}
                            </span>
                            <span>{(d.size / 1024).toFixed(1)} Ko · {(d.created_at || "").slice(0, 10)}</span>
                          </div>
                        </div>
                        <button onClick={() => downloadDoc(d)} title="Télécharger" className="p-1.5 rounded hover:bg-[#FAFAFA] text-[#06B6D4]"><Download size={12} /></button>
                        <button onClick={() => removeDoc(d)} title="Supprimer" className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 size={12} /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function KpiTile({ label, value, sub, color }) {
  return (
    <div className="bg-[#FAFAFA] border border-black/5 rounded-lg px-3 py-2">
      <div className="text-[9px] uppercase font-bold tracking-widest" style={{ color }}>{label}</div>
      <div className="text-lg font-bold text-[#0A1128] tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-[#64748B]">{sub}</div>}
    </div>
  );
}

function LoadingRow() {
  return <div className="text-xs text-[#64748B] text-center py-6 flex items-center justify-center gap-2"><Loader2 className="animate-spin" size={12} /> Chargement…</div>;
}

function RelatedList({ kind, items, companyId }) {
  if (!items || items.length === 0) {
    const empty = { appointments: "Aucun RDV lié.", proposals: "Aucun devis lié.", invoices: "Aucune facture liée.", projects: "Aucun projet en cours." }[kind];
    return <div className="text-xs text-[#64748B] text-center py-6">{empty}</div>;
  }
  return (
    <ul className="divide-y divide-black/5" data-testid={`adm-comp-list-${kind}-${companyId}`}>
      {items.map((it) => <RelatedRow key={it.id} kind={kind} it={it} />)}
    </ul>
  );
}

function RelatedRow({ kind, it }) {
  // Use window.location.origin so links use the current host (besson-solutions.fr in prod, preview domain in preview)
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pdfUrlInvoice = `${origin}/api/invoices/public/${it.id}/pdf`;
  const publicProposalUrl = it.public_token ? `${origin}/devis/${it.public_token}` : null;

  if (kind === "appointments") {
    const date = it.preferred_date || it.proposed_date || "—";
    const time = it.preferred_time || it.proposed_time || "";
    const statusTone = { confirme: "#A7F3D0", demande: "#FFDCA8", propose: "#FFDCA8", annule: "#FECACA", realise: "#06B6D4" }[it.status] || "#E5E7EB";
    return (
      <li className="flex items-center gap-2 py-2 text-sm">
        <CalIcon size={14} className="text-[#06B6D4] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-[#0A1128] truncate">{it.type} — {it.name}</div>
          <div className="text-[10px] text-[#64748B]">{date} {time}</div>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: statusTone, color: "#0A1128" }}>{it.status}</span>
      </li>
    );
  }
  if (kind === "proposals") {
    const ttc = (it.totals || {}).total_ttc || 0;
    const statusTone = { signe: "#A7F3D0", envoye: "#FFDCA8", brouillon: "#E5E7EB", refuse: "#FECACA", expire: "#FECACA", paye_acompte: "#A7F3D0", en_cours: "#06B6D4", livre: "#06B6D4", paye_solde: "#A7F3D0", cloture: "#A7F3D0" }[it.status] || "#E5E7EB";
    return (
      <li className="flex items-center gap-2 py-2 text-sm">
        <FileSignature size={14} className="text-[#06B6D4] flex-shrink-0" />
        <a href={`/admin/devis/${it.id}`} className="flex-1 min-w-0 group" title="Ouvrir le devis dans l'éditeur">
          <div className="font-bold text-[#0A1128] truncate group-hover:text-[#06B6D4]">{it.ref || "—"} — {(it.project || {}).title || "Sans titre"}</div>
          <div className="text-[10px] text-[#64748B]">{(it.created_at || "").slice(0, 10)} · {fmtEUR(ttc)} TTC</div>
        </a>
        {publicProposalUrl && (
          <a href={publicProposalUrl} target="_blank" rel="noreferrer" title="Voir la version cliente / signature" data-testid={`adm-comp-proposal-public-${it.id}`} className="p-1.5 rounded hover:bg-[#FAFAFA] text-[#06B6D4]" onClick={(e) => e.stopPropagation()}>
            <ExternalLink size={12} />
          </a>
        )}
        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: statusTone, color: "#0A1128" }}>{it.status}</span>
      </li>
    );
  }
  if (kind === "invoices") {
    const ttc = it.ttc || 0;
    const paid = it.paid;
    return (
      <li className="flex items-center gap-2 py-2 text-sm">
        <Receipt size={14} className="text-[#06B6D4] flex-shrink-0" />
        <a href={pdfUrlInvoice} target="_blank" rel="noreferrer" className="flex-1 min-w-0 group" data-testid={`adm-comp-invoice-link-${it.id}`} title="Ouvrir la facture PDF dans un nouvel onglet">
          <div className="font-bold text-[#0A1128] truncate group-hover:text-[#06B6D4]">{it.ref || "—"}</div>
          <div className="text-[10px] text-[#64748B]">{(it.created_at || "").slice(0, 10)} · {fmtEUR(ttc)} TTC{it.due_at ? ` · échéance ${it.due_at}` : ""}</div>
        </a>
        <a href={pdfUrlInvoice} target="_blank" rel="noreferrer" title="Télécharger le PDF" className="p-1.5 rounded hover:bg-[#FAFAFA] text-[#06B6D4]" onClick={(e) => e.stopPropagation()}>
          <ExternalLink size={12} />
        </a>
        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: paid ? "#A7F3D0" : "#FFDCA8", color: "#0A1128" }}>{paid ? "Payée" : "À régler"}</span>
      </li>
    );
  }
  if (kind === "projects") {
    const pct = it.progress_pct || 0;
    return (
      <li className="flex items-center gap-2 py-2 text-sm">
        <Briefcase size={14} className="text-[#06B6D4] flex-shrink-0" />
        <a href={`/admin/projets`} className="flex-1 min-w-0 group">
          <div className="font-bold text-[#0A1128] truncate group-hover:text-[#06B6D4]">{it.title}</div>
          <div className="text-[10px] text-[#64748B]">{it.status} · {it.tasks_done}/{it.tasks_total} tâches ({pct}%)</div>
        </a>
        <div className="w-16 h-1.5 bg-[#E5E7EB] rounded-full overflow-hidden flex-shrink-0">
          <div className="h-full bg-[#06B6D4] rounded-full" style={{ width: `${pct}%` }} />
        </div>
      </li>
    );
  }
  return null;
}

function CompanyEnrichment({ company }) {
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState(company.siret || company.name || "");
  const [results, setResults] = useState(null);
  const [picked, setPicked] = useState(null);

  const lookup = async () => {
    if (!query.trim()) return toast.error("SIRET, SIREN ou nom requis");
    setBusy(true);
    setResults(null);
    setPicked(null);
    try {
      const r = await api.get("/admin/company-lookup", { params: { q: query.trim() } });
      setResults(r.data.results || []);
      if ((r.data.results || []).length === 1) setPicked(r.data.results[0]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lookup impossible");
    } finally { setBusy(false); }
  };

  return (
    <div className="mb-3 bg-[#FAFAFA] rounded-xl p-3 border border-black/5" data-testid={`enrich-${company.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <Search size={14} className="text-[#06B6D4]" />
        <span className="text-xs font-bold tracking-widest uppercase text-[#06B6D4]">Fiche officielle (data.gouv.fr)</span>
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") lookup(); }}
          placeholder="SIRET (14), SIREN (9) ou nom de l'entreprise"
          className="flex-1 border border-black/10 rounded-md px-3 py-2 text-sm bg-white"
          data-testid={`enrich-input-${company.id}`}
        />
        <button onClick={lookup} disabled={busy} className="px-3 py-2 rounded-md bg-[#0A1128] text-white text-xs font-bold hover:bg-[#14213d] inline-flex items-center gap-1.5" data-testid={`enrich-search-${company.id}`}>
          {busy ? <Loader2 className="animate-spin" size={12} /> : <Search size={12} />} Enrichir
        </button>
      </div>

      {results !== null && results.length === 0 && (
        <div className="mt-2 text-xs text-[#92400E] bg-[#FFDCA8]/40 rounded p-2">Aucun résultat. Essayez avec le SIRET (14 chiffres), SIREN (9 chiffres) ou un mot-clé plus court.</div>
      )}

      {!picked && results && results.length > 1 && (
        <ul className="mt-2 space-y-1.5">
          {results.map((r) => (
            <li key={r.siren} className="bg-white border border-black/8 rounded-lg p-2 text-xs">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#0A1128] truncate">{r.nom_complet || r.denomination}</div>
                  <div className="text-[#64748B] truncate">SIREN {r.siren} · {r.commune || r.adresse}</div>
                </div>
                <button onClick={() => setPicked(r)} className="text-[#06B6D4] font-bold hover:underline">Choisir →</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {picked && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 bg-white border border-[#06B6D4]/40 rounded-xl p-3 text-xs" data-testid={`enrich-result-${company.id}`}>
          <Field label="Dénomination" value={picked.nom_complet || picked.denomination} />
          <Field label="Forme juridique" value={picked.forme_juridique} />
          <Field label="SIREN" value={picked.siren} />
          <Field label="SIRET (siège)" value={picked.siret} />
          <Field label="Code NAF" value={picked.activite_principale || picked.code_naf} />
          <Field label="Effectif" value={picked.tranche_effectif_salarie} />
          <Field label="Création" value={picked.date_creation} />
          <Field label="État" value={picked.etat_administratif === "A" ? "Active" : (picked.etat_administratif || "—")} />
          <div className="sm:col-span-2">
            <Field label="Adresse" value={picked.adresse} icon={MapPin} />
          </div>
          {picked.dirigeants?.length > 0 && (
            <div className="sm:col-span-2">
              <div className="text-[10px] font-bold tracking-widest uppercase text-[#64748B] mb-1 flex items-center gap-1"><UsersIcon size={10} /> Dirigeants</div>
              <ul className="space-y-0.5">
                {picked.dirigeants.map((d, i) => (
                  <li key={i} className="text-[#0A1128]">
                    <b>{d.prenoms} {d.nom}</b> {d.qualite && <span className="text-[#64748B]">— {d.qualite}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="sm:col-span-2 flex flex-wrap gap-2 mt-1 pt-2 border-t border-black/8">
            <a href={picked.sources.annuaire} target="_blank" rel="noreferrer" className="text-[#06B6D4] inline-flex items-center gap-1 font-bold hover:underline">Annuaire entreprises <ExternalLink size={10} /></a>
            <a href={picked.sources.pappers} target="_blank" rel="noreferrer" className="text-[#06B6D4] inline-flex items-center gap-1 font-bold hover:underline">Pappers <ExternalLink size={10} /></a>
            <a href={picked.sources.societe} target="_blank" rel="noreferrer" className="text-[#06B6D4] inline-flex items-center gap-1 font-bold hover:underline">Societe.com <ExternalLink size={10} /></a>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, icon: Icon }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[9px] font-bold tracking-widest uppercase text-[#64748B] flex items-center gap-1">{Icon && <Icon size={10} />} {label}</div>
      <div className="text-[#0A1128] font-medium">{value}</div>
    </div>
  );
}


/* -------- ClientTimeline: chronological activity feed -------- */
const TIMELINE_META = {
  proposal:        { icon: FileSignature,  color: "#06B6D4", label: "Devis créé" },
  proposal_sent:   { icon: Mail,           color: "#06B6D4", label: "Devis envoyé" },
  proposal_signed: { icon: CheckCircle2,   color: "#16a34a", label: "Devis signé" },
  invoice:         { icon: Receipt,        color: "#0A1128", label: "Facture émise" },
  invoice_paid:    { icon: Euro,           color: "#16a34a", label: "Facture payée" },
  appointment:     { icon: CalIcon,        color: "#FFDCA8", label: "Rendez-vous" },
  brief:           { icon: FilePlus,       color: "#FFDCA8", label: "Brief" },
  document:        { icon: FolderUp,       color: "#94a3b8", label: "Document" },
};

function ClientTimeline({ events }) {
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(false);

  const filtered = useMemo(() => {
    const list = filter === "all" ? events : events.filter((e) => e.type === filter || e.type.startsWith(filter));
    return expanded ? list : list.slice(0, 12);
  }, [events, filter, expanded]);

  const types = useMemo(() => {
    const c = {};
    events.forEach((e) => { c[e.type] = (c[e.type] || 0) + 1; });
    return c;
  }, [events]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" }) +
             " · " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    } catch { return iso.slice(0, 16).replace("T", " "); }
  };

  return (
    <div data-testid="client-timeline">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="text-xs font-bold tracking-widest uppercase text-[#06B6D4] flex items-center gap-1.5">
          <History size={12} /> Historique complet ({events.length})
        </div>
        <div className="flex gap-1 flex-wrap">
          <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label={`Tout (${events.length})`} testId="tl-filter-all" />
          {Object.keys(types).map((t) => {
            const meta = TIMELINE_META[t] || { label: t, color: "#94a3b8" };
            return <FilterChip key={t} active={filter === t} onClick={() => setFilter(t)} label={`${meta.label} (${types[t]})`} color={meta.color} testId={`tl-filter-${t}`} />;
          })}
        </div>
      </div>
      <div className="relative pl-5 border-l-2 border-dashed border-[#06B6D4]/30">
        {filtered.length === 0 ? (
          <div className="text-sm text-[#64748B] italic py-3">Aucun événement de ce type.</div>
        ) : filtered.map((e, i) => {
          const meta = TIMELINE_META[e.type] || { icon: Activity, color: "#94a3b8", label: e.type };
          const Icon = meta.icon;
          return (
            <div key={`${e.type}-${e.id || ''}-${i}-${e.at}`} className="relative mb-3" data-testid={`timeline-event-${e.type}`}>
              <span
                className="absolute -left-[27px] top-1.5 w-4 h-4 rounded-full flex items-center justify-center ring-4 ring-white"
                style={{ background: meta.color }}
              >
                <Icon size={8} className="text-white" />
              </span>
              <div className="bg-white border border-black/8 rounded-xl p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="display font-bold text-[#0A1128] text-sm">{e.title}</div>
                  {e.amount != null && <span className="text-[11px] font-extrabold text-[#0A1128] tabular-nums">{fmtEUR(e.amount)}</span>}
                </div>
                <div className="text-[11px] text-[#64748B] mt-0.5 flex items-center gap-2 flex-wrap">
                  <span>{fmtDate(e.at)}</span>
                  {e.status && <span className="inline-block px-1.5 py-0 rounded bg-[#FAFAFA] text-[10px] font-bold uppercase">{e.status}</span>}
                  {e.source && <span className="text-[10px]">par {e.source}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {events.length > 12 && filter === "all" && (
        <button onClick={() => setExpanded(!expanded)} data-testid="timeline-expand-toggle" className="mt-2 text-xs font-bold text-[#06B6D4] hover:underline">
          {expanded ? "Réduire" : `Voir tout (${events.length - 12} de plus)`}
        </button>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, label, color, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border transition-colors ${active ? "bg-[#0A1128] text-white border-[#0A1128]" : "bg-white text-[#64748B] border-black/10 hover:border-[#06B6D4]"}`}
      style={active && color ? { background: color, borderColor: color, color: "#0A1128" } : {}}
    >
      {label}
    </button>
  );
}


const EVENT_KINDS = [
  { v: "appel", l: "Appel téléphonique" },
  { v: "rdv_physique", l: "RDV physique" },
  { v: "visio", l: "Visio" },
  { v: "sms", l: "SMS" },
  { v: "email", l: "Email" },
  { v: "note", l: "Note interne" },
  { v: "autre", l: "Autre" },
];

function NewClientEventButton({ entrepreneurId, companies = [], onCreated }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("appel");
  const [note, setNote] = useState("");
  const [happenedAt, setHappenedAt] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!note.trim() && kind !== "appel" && kind !== "sms" && kind !== "email") return toast.error("Note requise");
    if (companies.length > 1 && !companyId) return toast.error("Sélectionnez l'entreprise concernée");
    setBusy(true);
    try {
      await api.post(`/admin/entrepreneurs/${entrepreneurId}/events`, {
        kind, note, happened_at: happenedAt || null,
        company_id: companyId || (companies.length === 1 ? companies[0].id : null),
      });
      toast.success("Événement enregistré");
      setOpen(false); setNote(""); setHappenedAt(""); setKind("appel"); setCompanyId("");
      onCreated && onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="client-event-add" className="bg-[#06B6D4] text-[#0A1128] hover:bg-[#67E8F9] font-bold rounded-xl px-4 py-2 text-sm inline-flex items-center gap-1.5 shadow-sm">
        <Activity size={14} />Saisir un événement
      </button>
      {open && (
        <div className="fixed inset-0 z-[90] bg-[#0A1128]/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-5 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="display text-lg font-extrabold text-[#0A1128]">Nouvel événement / échange</h3>
            <p className="text-xs text-[#64748B] mt-1 mb-3">Sera ajouté à l'historique client (timeline).</p>
            <div className="space-y-3">
              {companies.length > 0 && (
                <div data-testid="client-event-company-picker">
                  <label className="text-xs font-bold text-[#64748B] uppercase tracking-widest flex items-center gap-1.5 mb-1.5">
                    <Briefcase size={11} className="text-[#06B6D4]" />Pour quelle entreprise ? {companies.length > 1 && <span className="text-red-500">*</span>}
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {companies.map((c) => (
                      <button key={c.id} type="button" onClick={() => setCompanyId(c.id)} data-testid={`client-event-company-${c.id}`} className={`text-left px-2.5 py-1.5 rounded-lg border transition-colors text-xs ${companyId === c.id ? "bg-[#06B6D4] text-[#0A1128] border-[#06B6D4] font-bold" : "bg-white border-black/10 hover:border-[#06B6D4]"}`}>
                        <div className="font-bold truncate">{c.name}</div>
                        <div className="text-[10px] opacity-80 truncate">{c.sector || c.siret || ""}</div>
                      </button>
                    ))}
                    {companies.length > 1 && (
                      <button type="button" onClick={() => setCompanyId("")} data-testid="client-event-company-none" className={`text-left px-2.5 py-1.5 rounded-lg border transition-colors text-xs italic ${companyId === "" ? "bg-[#FFDCA8] text-[#92400E] border-[#FFDCA8] font-bold" : "bg-white border-black/10 hover:border-[#FFDCA8]"}`}>
                        <div className="font-bold">Général / Toutes</div>
                        <div className="text-[10px] opacity-80">Pas spécifique</div>
                      </button>
                    )}
                  </div>
                </div>
              )}
              <div>
                <label className="text-xs font-bold text-[#64748B] uppercase tracking-widest">Type</label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-1.5">
                  {EVENT_KINDS.map((k) => (
                    <button key={k.v} type="button" onClick={() => setKind(k.v)} data-testid={`client-event-kind-${k.v}`} className={`text-xs font-bold px-2 py-1.5 rounded-lg border transition-colors ${kind === k.v ? "bg-[#06B6D4] text-[#0A1128] border-[#06B6D4]" : "bg-white text-[#64748B] border-black/10 hover:border-[#06B6D4]"}`}>{k.l}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-[#64748B] uppercase tracking-widest">Date</label>
                <Input type="datetime-local" value={happenedAt} onChange={(e) => setHappenedAt(e.target.value)} data-testid="client-event-date" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#64748B] uppercase tracking-widest">Description / note</label>
                <textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} data-testid="client-event-note" className="w-full border border-input rounded-md px-3 py-2 text-sm" placeholder="Détails de l'échange…" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={submit} disabled={busy} className="bg-[#0A1128] text-white" data-testid="client-event-submit">{busy ? "Enregistrement…" : "Enregistrer"}</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ====== Create Client Modal (admin onboards a client + companies + welcome email) ====== */
function CreateClientModal({ onClose, onCreated }) {
  const [step, setStep] = useState(1); // 1 = client info, 2 = companies, 3 = confirm
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", role_title: "", send_welcome: true });
  const [companies, setCompanies] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const addCompany = (c) => setCompanies((prev) => [...prev, c]);
  const removeCompany = (idx) => setCompanies((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!form.full_name || !form.email) return toast.error("Nom et email requis");
    setBusy(true);
    try {
      const r = await api.post("/admin/entrepreneurs", { ...form, companies });
      setResult(r.data);
      setStep(3);
      toast.success(r.data.email_sent ? "Compte créé et email envoyé" : "Compte créé (email non envoyé — vérifier la config SMTP/Resend)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const copyLink = () => {
    const url = `${window.location.origin}${result.welcome_link}`;
    navigator.clipboard.writeText(url);
    toast.success("Lien copié");
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col overflow-hidden p-0">
        <DialogHeader className="px-5 sm:px-6 pt-5 pb-3 border-b border-black/8 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <UserPlus size={18} className="text-[#06B6D4]" />
            {step === 3 ? "Compte client créé" : "Créer un compte client"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4">
        {/* Stepper */}
        {step < 3 && (
          <div className="flex items-center gap-2 text-xs mb-4">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center font-bold shrink-0 ${step >= 1 ? "bg-[#06B6D4] text-[#0A1128]" : "bg-black/10 text-[#64748B]"}`}>1</span>
            <span className={`truncate ${step >= 1 ? "font-bold text-[#0A1128]" : "text-[#64748B]"}`}>Informations client</span>
            <div className="flex-1 h-px bg-black/10 min-w-[12px]" />
            <span className={`w-6 h-6 rounded-full flex items-center justify-center font-bold shrink-0 ${step >= 2 ? "bg-[#06B6D4] text-[#0A1128]" : "bg-black/10 text-[#64748B]"}`}>2</span>
            <span className={`truncate ${step >= 2 ? "font-bold text-[#0A1128]" : "text-[#64748B]"}`}>Entreprises (optionnel)</span>
          </div>
        )}

        {/* STEP 1 — Client info */}
        {step === 1 && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Prénom & Nom *</Label><Input data-testid="new-client-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Ex : Marie Dupont" /></div>
              <div><Label>Email *</Label><Input data-testid="new-client-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="contact@..." /></div>
              <div><Label>Téléphone</Label><Input data-testid="new-client-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="06 12 34 56 78" /></div>
              <div><Label>Fonction</Label><Input data-testid="new-client-title" value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} placeholder="Dirigeant, DG, Resp. marketing…" /></div>
            </div>
            <div className="bg-[#FAFAFA] border border-black/8 rounded-xl p-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <MailIcon size={16} className="text-[#06B6D4]" />
                <div>
                  <Label className="cursor-pointer">Envoyer l'email de bienvenue</Label>
                  <div className="text-[11px] text-[#64748B]">Un lien valide 7 jours sera envoyé pour définir le mot de passe.</div>
                </div>
              </div>
              <Switch checked={form.send_welcome} onCheckedChange={(v) => setForm({ ...form, send_welcome: v })} data-testid="new-client-send-welcome" />
            </div>
            <DialogFooter className="!mt-5">
              <Button variant="outline" onClick={onClose}>Annuler</Button>
              <Button onClick={() => { if (!form.full_name || !form.email) return toast.error("Nom et email requis"); setStep(2); }} className="bg-[#0A1128] text-white" data-testid="new-client-step-2">
                Suivant : ajouter des entreprises →
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* STEP 2 — Companies */}
        {step === 2 && (
          <div className="space-y-3">
            <div className="text-sm text-[#64748B] -mt-2 mb-2">
              Vous pouvez ajouter 0, 1 ou plusieurs entreprises. Le client pourra en ajouter d'autres depuis son espace après connexion.
            </div>
            <AdminCompanyAdder onAdd={(c) => addCompany(c)} />
            {companies.length > 0 && (
              <div className="space-y-2 pt-3 border-t border-black/8">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#64748B]">Entreprises à créer ({companies.length})</div>
                {companies.map((c, i) => (
                  <div key={i} className="bg-[#06B6D4]/5 border border-[#06B6D4]/20 rounded-xl p-3 flex items-center justify-between" data-testid={`new-client-company-${i}`}>
                    <div className="min-w-0">
                      <div className="font-bold text-[#0A1128] truncate flex items-center gap-2"><Briefcase size={13} className="text-[#06B6D4]" />{c.name}</div>
                      <div className="text-[11px] text-[#64748B] truncate">{c.siret ? `SIRET ${c.siret} · ` : ""}{c.sector || ""}{c.address ? ` · ${c.address}` : ""}</div>
                    </div>
                    <button onClick={() => removeCompany(i)} className="text-[#94a3b8] hover:text-red-500 p-1"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}
            <DialogFooter className="!mt-5">
              <Button variant="outline" onClick={() => setStep(1)}>← Retour</Button>
              <Button onClick={submit} disabled={busy} className="bg-[#06B6D4] text-[#0A1128] font-bold" data-testid="new-client-submit">
                {busy ? "Création…" : `Créer le compte${form.send_welcome ? " + envoyer email" : ""}`}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* STEP 3 — Result */}
        {step === 3 && result && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-start gap-3" data-testid="new-client-success">
              <CheckCircle2 size={20} className="text-green-600 shrink-0 mt-0.5" />
              <div className="text-sm text-[#0A1128]">
                <div className="font-extrabold">Compte créé avec succès !</div>
                <div className="text-[#475569] mt-1">{result.entrepreneur?.email}</div>
                <div className="text-xs text-[#64748B] mt-1">{(result.companies || []).length} entreprise(s) rattachée(s)</div>
              </div>
            </div>
            <div className="bg-[#FAFAFA] border border-black/8 rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#64748B] mb-2">Email de bienvenue</div>
              {result.email_sent ? (
                <div className="text-sm text-green-700 font-bold flex items-center gap-2"><MailIcon size={14} />Email envoyé à {result.entrepreneur?.email}</div>
              ) : (
                <>
                  <div className="text-sm text-orange-700 font-bold flex items-start gap-2"><MailIcon size={14} className="mt-0.5 shrink-0" /><span>Email non envoyé.{result.email_error ? <span className="font-normal block text-[11px] text-orange-600 mt-0.5">{result.email_error}</span> : null} Transmettez le lien manuellement :</span></div>
                </>
              )}
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 text-[11px] bg-white border border-black/10 rounded px-2 py-1.5 truncate font-mono">{window.location.origin}{result.welcome_link}</code>
                <button onClick={copyLink} className="text-[#06B6D4] hover:text-[#67E8F9] p-1.5" title="Copier"><Copy size={14} /></button>
              </div>
              <div className="text-[10px] text-[#94a3b8] mt-1">Lien valide 7 jours.</div>
            </div>
            <DialogFooter>
              <Button onClick={onCreated} className="bg-[#0A1128] text-white" data-testid="new-client-done">Terminé</Button>
            </DialogFooter>
          </div>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AdminCompanyAdder({ onAdd }) {
  const [mode, setMode] = useState("siret"); // siret | manual
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState({ name: "", siret: "", sector: "", address: "", phone: "", email: "", website: "" });
  const [related, setRelated] = useState(null); // { dirigeant: {nom, prenoms}, items: [...] }
  const [relatedBusy, setRelatedBusy] = useState(false);

  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const cleaned = q.replace(/\s/g, "");
      const params = /^\d{14}$/.test(cleaned) ? `siret=${cleaned}` : /^\d{9}$/.test(cleaned) ? `siren=${cleaned}` : `q=${encodeURIComponent(q)}`;
      const r = await api.get(`/admin/company-lookup?${params}`);
      setResults(r.data.results || []);
      if ((r.data.results || []).length === 0) toast.message("Aucun résultat. Saisie manuelle possible.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Recherche indisponible"); }
    finally { setBusy(false); }
  };

  const fetchRelated = async (dirigeant, sirenToExclude) => {
    if (!dirigeant?.nom) return;
    setRelatedBusy(true);
    setRelated({ dirigeant, items: [] });
    try {
      const r = await api.get("/admin/related-companies", { params: { nom: dirigeant.nom, prenoms: dirigeant.prenoms || "", exclude_siren: sirenToExclude || "" } });
      setRelated({ dirigeant, items: r.data.results || [] });
    } catch { setRelated({ dirigeant, items: [] }); }
    finally { setRelatedBusy(false); }
  };

  const pickFromResult = (r) => {
    onAdd({
      name: r.nom_complet || r.denomination || "",
      siret: r.siret || "",
      sector: r.section_activite || r.activite_principale || "",
      address: r.adresse ? `${r.adresse}${r.code_postal ? `, ${r.code_postal}` : ""}${r.commune ? ` ${r.commune}` : ""}`.trim() : "",
      website: r.site_internet || "",
    });
    setQ(""); setResults([]);
    toast.success(`${r.nom_complet || r.denomination} ajoutée`);
    // Auto-detect related companies if a dirigeant is present
    if (r.dirigeants && r.dirigeants[0]) fetchRelated(r.dirigeants[0], r.siren);
  };

  const pickFromRelated = (r) => {
    onAdd({
      name: r.nom_complet || "",
      siret: r.siret || "",
      sector: r.section_activite || r.activite_principale || "",
      address: r.adresse ? `${r.adresse}${r.code_postal ? `, ${r.code_postal}` : ""}${r.commune ? ` ${r.commune}` : ""}`.trim() : "",
      website: r.site_internet || "",
    });
    setRelated((prev) => prev ? { ...prev, items: prev.items.filter((x) => x.siren !== r.siren) } : null);
    toast.success(`${r.nom_complet} ajoutée`);
  };

  const addManual = () => {
    if (!manual.name) return toast.error("Nom requis");
    onAdd({ ...manual });
    setManual({ name: "", siret: "", sector: "", address: "", phone: "", email: "", website: "" });
    toast.success(`${manual.name} ajoutée`);
  };

  return (
    <div className="bg-gradient-to-br from-[#06B6D4]/10 to-[#67E8F9]/5 border border-[#06B6D4]/20 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="display font-extrabold text-[#0A1128] text-sm">Ajouter une entreprise</div>
        <div className="inline-flex rounded-lg border border-black/10 overflow-hidden text-xs">
          <button type="button" onClick={() => setMode("siret")} data-testid="adder-mode-siret" className={`px-2.5 py-1 font-bold ${mode === "siret" ? "bg-[#06B6D4] text-[#0A1128]" : "bg-white text-[#64748B]"}`}>Recherche SIRET</button>
          <button type="button" onClick={() => setMode("manual")} data-testid="adder-mode-manual" className={`px-2.5 py-1 font-bold ${mode === "manual" ? "bg-[#06B6D4] text-[#0A1128]" : "bg-white text-[#64748B]"}`}>Manuel</button>
        </div>
      </div>

      {mode === "siret" && (
        <>
          <div className="flex gap-2">
            <Input data-testid="adder-siret-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }} placeholder="SIRET (14), SIREN (9) ou nom" />
            <Button type="button" onClick={search} disabled={busy || !q.trim()} className="bg-[#0A1128] text-white" data-testid="adder-siret-search">
              {busy ? <Loader2 className="animate-spin" size={14} /> : "Chercher"}
            </Button>
          </div>
          {results.length > 0 && (
            <ul className="mt-2 space-y-1.5 max-h-48 overflow-y-auto">
              {results.map((r) => (
                <li key={r.siren}>
                  <button type="button" onClick={() => pickFromResult(r)} data-testid={`adder-siret-result-${r.siren}`} className="w-full text-left bg-white border border-black/8 rounded-lg p-2 hover:border-[#06B6D4] hover:bg-[#06B6D4]/5 transition-colors">
                    <div className="font-bold text-[#0A1128] text-sm truncate">{r.nom_complet || r.denomination}</div>
                    <div className="text-[10px] text-[#64748B] truncate">SIRET {r.siret} · {r.commune || ""}{r.dirigeants && r.dirigeants[0] ? ` · 👤 ${r.dirigeants[0].prenoms} ${r.dirigeants[0].nom}` : ""}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {mode === "manual" && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Nom *</Label><Input data-testid="adder-manual-name" value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} placeholder="Nom de l'entreprise" /></div>
            <div><Label className="text-xs">SIRET</Label><Input value={manual.siret} onChange={(e) => setManual({ ...manual, siret: e.target.value })} placeholder="Optionnel (en création…)" /></div>
            <div><Label className="text-xs">Secteur</Label><Input value={manual.sector} onChange={(e) => setManual({ ...manual, sector: e.target.value })} /></div>
            <div><Label className="text-xs">Téléphone</Label><Input value={manual.phone} onChange={(e) => setManual({ ...manual, phone: e.target.value })} /></div>
            <div className="col-span-2"><Label className="text-xs">Adresse</Label><Input value={manual.address} onChange={(e) => setManual({ ...manual, address: e.target.value })} /></div>
          </div>
          <Button type="button" onClick={addManual} disabled={!manual.name} className="bg-[#06B6D4] text-[#0A1128] w-full mt-1" data-testid="adder-manual-add">
            <Plus size={13} className="mr-1.5" />Ajouter cette entreprise à la liste
          </Button>
        </div>
      )}

      {/* Related companies (auto-detected from dirigeant) */}
      {related && (
        <div className="mt-3 pt-3 border-t border-[#06B6D4]/30" data-testid="adder-related">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="display font-extrabold text-[#0A1128] text-sm flex items-center gap-1.5">
                <Sparkles size={14} className="text-[#06B6D4]" />Autres mandats de {related.dirigeant.prenoms} {related.dirigeant.nom}
              </div>
              <div className="text-[10px] text-[#64748B]">Entreprises où cette personne a un mandat actif (source : Annuaire des Entreprises). Vérifiez les homonymes.</div>
            </div>
            <button type="button" onClick={() => setRelated(null)} className="text-[#94a3b8] hover:text-[#0A1128] p-1"><X size={14} /></button>
          </div>
          {relatedBusy ? (
            <div className="text-xs text-[#64748B] py-3 text-center"><Loader2 className="animate-spin inline mr-1" size={12} />Recherche en cours…</div>
          ) : related.items.length === 0 ? (
            <div className="text-xs text-[#64748B] py-3 text-center">Aucun autre mandat trouvé.</div>
          ) : (
            <ul className="space-y-1.5 max-h-56 overflow-y-auto">
              {related.items.map((r) => (
                <li key={r.siren}>
                  <button type="button" onClick={() => pickFromRelated(r)} data-testid={`adder-related-${r.siren}`} className="w-full text-left bg-white border border-black/8 rounded-lg p-2 hover:border-[#06B6D4] hover:bg-[#06B6D4]/5 transition-colors flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-[#0A1128] text-sm truncate">{r.nom_complet}</div>
                      <div className="text-[10px] text-[#64748B] truncate">SIREN {r.siren} · {r.role} · {r.commune || ""}</div>
                    </div>
                    <span className="shrink-0 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-[#06B6D4] text-[#0A1128]"><Plus size={10} className="inline" />Ajouter</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function MergeClientsModal({ onClose, onMerged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [primaryByGroup, setPrimaryByGroup] = useState({}); // group_idx -> entrepreneur id chosen as primary
  const [busyGroup, setBusyGroup] = useState(null);

  useEffect(() => {
    api.get("/admin/entrepreneurs/duplicates")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  }, []);

  const mergeGroup = async (groupIdx, group) => {
    const primary = primaryByGroup[groupIdx];
    if (!primary) return toast.error("Choisissez le compte à conserver (primary)");
    if (!window.confirm(`Fusionner ${group.length - 1} compte(s) dans ${group.find((g) => g.id === primary)?.email} ? Les données seront déplacées, puis les doublons supprimés.`)) return;
    setBusyGroup(groupIdx);
    try {
      const secondaries = group.filter((g) => g.id !== primary);
      for (const sec of secondaries) {
        await api.post(`/admin/entrepreneurs/${primary}/merge`, { secondary_id: sec.id });
      }
      toast.success(`✅ ${secondaries.length} compte(s) fusionné(s) dans le primary`);
      onMerged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusyGroup(null); }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-[#0A1128]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="merge-clients-modal">
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[88vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-black/8 flex items-center justify-between">
          <div>
            <h2 className="display text-lg font-extrabold text-[#0A1128]">Fusionner les doublons clients</h2>
            <p className="text-xs text-[#64748B] mt-0.5">Détection par email exact et par nom normalisé. Choisissez le compte principal puis fusionnez — les données du compte secondaire seront déplacées vers le principal.</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-black/5" aria-label="Fermer"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {loading && <div className="text-center text-sm text-[#64748B] py-8">Recherche des doublons…</div>}
          {!loading && data && data.total_groups === 0 && (
            <div className="text-center text-sm text-[#64748B] py-12">
              <CheckCircle2 size={32} className="mx-auto mb-2 text-[#10B981]" />
              <p className="font-bold text-[#0A1128]">Aucun doublon détecté ✨</p>
              <p className="text-xs mt-1">Votre base est propre.</p>
            </div>
          )}
          {!loading && data && data.groups.map((group, idx) => (
            <div key={idx} className="border border-[#FFDCA8]/60 bg-[#FFDCA8]/8 rounded-2xl p-4" data-testid={`merge-group-${idx}`}>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#92400E] mb-2">Groupe #{idx + 1} · {group.length} comptes potentiellement identiques</div>
              <div className="space-y-1.5">
                {group.map((e) => (
                  <label key={e.id} className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors ${primaryByGroup[idx] === e.id ? "bg-[#06B6D4]/10 border-[#06B6D4]" : "bg-white border-black/10 hover:border-[#06B6D4]/40"}`}>
                    <input
                      type="radio"
                      name={`primary-${idx}`}
                      checked={primaryByGroup[idx] === e.id}
                      onChange={() => setPrimaryByGroup({ ...primaryByGroup, [idx]: e.id })}
                      data-testid={`merge-pick-${idx}-${e.id}`}
                      className="w-4 h-4 accent-[#06B6D4]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-[#0A1128] truncate text-sm">{e.full_name || "—"}</div>
                      <div className="text-[11px] text-[#64748B] truncate">{e.email} · {e.phone || "Pas de téléphone"}</div>
                      <div className="text-[10px] text-[#94a3b8]">Créé le {(e.created_at || "").slice(0, 10)}</div>
                    </div>
                    {primaryByGroup[idx] === e.id && <span className="text-[10px] font-bold text-[#06B6D4] uppercase tracking-widest">À conserver</span>}
                  </label>
                ))}
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <Button
                  onClick={() => mergeGroup(idx, group)}
                  disabled={busyGroup === idx || !primaryByGroup[idx]}
                  className="bg-[#0A1128] hover:bg-[#14213d] text-white text-xs"
                  data-testid={`merge-confirm-${idx}`}
                >
                  {busyGroup === idx ? "Fusion…" : `Fusionner ce groupe (${group.length - 1} doublons → 1)`}
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="px-6 py-3 border-t border-black/8 flex justify-end">
          <Button onClick={onClose} variant="outline" data-testid="merge-close">Fermer</Button>
        </div>
      </div>
    </div>
  );
}

